import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { FileText, Loader2, Paperclip, Reply, SendHorizontal, Smile, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { insertAtCaret, useComposerBridge } from '@/features/chat/composer-bridge';
import { quotedPreviewText, quotedSenderLabel } from '@/features/chat/quote-preview';
import { useSendMessage } from '@/features/chat/useSendMessage';
import { cn } from '@/lib/utils';
import { usePrefsStore } from '@/store/prefs';

import type { Message } from '@/features/chat/chat.api';

/** Lazy-loaded so the ~760-entry emoji dataset ships as its own chunk,
 *  fetched the first time an operator opens the picker — never in the main
 *  bundle. */
const EmojiPicker = lazy(() => import('./EmojiPicker'));

interface ComposerProps {
  /** The conversation a send targets. In thread mode (ThreadPanel) this is
   *  the CURRENTLY CHOSEN member conversation — it changes when the account
   *  picker switches, and `handleSend` reads it at submit time, which is
   *  exactly the "bind the target at submit" contract. The draft-wipe
   *  effect keys on `draftResetKey ?? conversationId`, so a picker switch
   *  never clears a half-typed draft (pass a stable `draftResetKey`). */
  conversationId: string;
  /** Identity of the DRAFT scope. Defaults to `conversationId` (the classic
   *  single-conversation behavior: switching chats clears the draft). Thread
   *  mode passes the stable thread key so the draft survives account
   *  switching within one thread. */
  draftResetKey?: string;
  /** Merged-thread cache key — forwarded to `useSendMessage` so the
   *  optimistic bubble / success patch / failure mark also land in the
   *  thread's messages cache. `undefined`/`null` = classic single-
   *  conversation send. */
  threadKey?: string | null;
  /** The sending account's UUID (thread mode) — stamped on the optimistic
   *  message so the per-bubble account chip renders immediately. */
  accountId?: string | null;
  /** Extra line under the quoted-message preview, shown only while a reply is
   *  armed. ThreadPanel uses it to explain that a quoted reply is pinned to
   *  the quoted message's own account — the constraint belongs next to the
   *  QUOTE, not next to the account picker (which lives in the thread header
   *  and would jump the header's height if it carried this). */
  replyHint?: React.ReactNode;
  /** Message the console agent picked "回复" on, or `null` when not
   *  replying. Owned by `ChatPanelInner` (not local state) so a context-menu
   *  pick anywhere in the message list can populate it. */
  replyingTo?: Message | null;
  /** Clears `replyingTo` on the parent — called on cancel-×, on send, and
   *  from this component's own Escape handling. */
  onCancelReply?: () => void;
}

/** Same cap as `hub/app/api/messages.py::MAX_UPLOAD_BYTES`. Mirroring it
 *  here gives instant client-side feedback rather than a 413 round-trip.
 *  16 MB is the WhatsApp Business API media ceiling — platform-derived, not
 *  an arbitrary choice. Uploading more would succeed at the Hub but be
 *  rejected by WhatsApp's servers before delivery. */
const MAX_UPLOAD_BYTES = 16 * 1024 * 1024;

/** Hard cap on the textarea height — about 6 lines. Beyond this the
 *  textarea grows a scrollbar instead of pushing the composer up. */
const MAX_TEXTAREA_PX = 144;

/**
 * Bottom of the chat panel — text input, attachment chip, send button.
 *
 *   - Enter sends; Shift+Enter inserts a newline.
 *   - Attachment is a hidden `<input type="file">` triggered by a
 *     paperclip Button. Selected file shows as a chip above the
 *     textarea with a remove (×) button.
 *   - Send disabled when both text and file are empty.
 *   - Client-side size cap (25 MB) shows an inline toast.
 *   - Reply banner: shown above the textarea when `replyingTo` is set
 *     (picked via a message's "回复" context-menu item). Sending forwards
 *     the quoted message's `platform_message_id` as `reply_to`.
 *   - Emoji picker: a Smile toggle (left of the paperclip, WhatsApp order)
 *     opens a lazy-loaded panel anchored above the strip. Picking inserts at
 *     the textarea caret and keeps the panel open (rapid multi-pick); it
 *     closes on toggle click, outside mousedown, Escape, or a draft-scope
 *     switch.
 *   - Profile inserts: the customer-profile sidebar's 「插入」 buttons queue
 *     text on the composer bridge (`composer-bridge.ts`). It is spliced in
 *     at the caret without replacing any draft text, the textarea takes
 *     focus, and NOTHING is sent — the agent reviews and sends. A request
 *     for another conversation is refused with a toast; a draft-scope
 *     switch drops whatever is still pending.
 *   - Escape: emoji panel open → close it (nothing else). Otherwise: reply
 *     banner or attachment present → clear it. Otherwise blur the textarea.
 *     Either way `stopPropagation()`s so `ChatPanelInner`'s document-level
 *     Escape handler (which closes the conversation) only ever sees an
 *     Escape press that did NOT originate from a focused composer.
 */
export function Composer({
  conversationId,
  draftResetKey,
  threadKey = null,
  accountId = null,
  replyHint,
  replyingTo = null,
  onCancelReply,
}: ComposerProps): React.JSX.Element {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const emojiPanelRef = useRef<HTMLDivElement | null>(null);
  /** Caret index to restore after an emoji or profile insert commits — see
   *  the layout effect below. `null` = nothing pending. */
  const pendingCaretRef = useRef<number | null>(null);

  const send = useSendMessage();

  // Whether Enter sends. Defaults to `true` (the classic behavior); the
  // Settings modal can flip it off so Enter inserts a newline instead.
  const enterSend = usePrefsStore((s) => s.enterSend);

  // Blob URL for image thumbnail preview — owned by this effect so that
  // each URL is revoked exactly once when the file changes or the component
  // unmounts. Using a closure capture (not a ref) means React 18 StrictMode's
  // double-invoke (create→cleanup→create) is safe: each invocation pair
  // operates on its own captured url string.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Auto-grow the textarea up to MAX_TEXTAREA_PX.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const desired = Math.min(el.scrollHeight, MAX_TEXTAREA_PX);
    el.style.height = `${desired}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_PX ? 'auto' : 'hidden';
  }, [text]);

  // Reset when the DRAFT SCOPE switches so a half-typed draft doesn't bleed
  // across chats. The scope is the conversation id by default; thread mode
  // passes the stable thread key as `draftResetKey` so switching the target
  // ACCOUNT within one thread (which swaps `conversationId`) keeps the
  // draft. (A real drafts feature would persist per-scope; out of scope.)
  // The emoji panel closes too — it belongs to the abandoned draft context,
  // and so does any profile insert still queued on the composer bridge.
  const draftScope = draftResetKey ?? conversationId;
  useEffect(() => {
    setText('');
    setFile(null);
    setEmojiOpen(false);
    const { requests, consume } = useComposerBridge.getState();
    for (const request of requests) consume(request.id);
  }, [draftScope]);

  // Restore the caret after an emoji or profile insert. `text` is
  // controlled, so the new value only reaches the DOM when React commits —
  // and committing a controlled value resets the browser caret to the end.
  // This layout effect runs synchronously after that DOM mutation and moves
  // the caret to just after the inserted text (index computed in UTF-16
  // units by `handleEmojiPick` / `insertAtCaret`), before the browser paints.
  useLayoutEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret === null) return;
    pendingCaretRef.current = null;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(caret, caret);
  }, [text]);

  // Profile-sidebar inserts (composer bridge). Declared AFTER the draft-scope
  // reset: when a scope switch and a request land in one commit, the reset
  // has already consumed the request, so the previous scope's draft (still
  // in the DOM at this point) is never spliced into and carried over. Reads
  // the LIVE queue rather than the rendered snapshot, so each request is
  // consumed — and inserted — exactly once. The click that queued it moved
  // focus to the sidebar, which already ended any IME composition here.
  const insertRequests = useComposerBridge((s) => s.requests);
  useEffect(() => {
    if (insertRequests.length === 0) return;
    const el = textareaRef.current;
    if (!el) return;
    const { requests, consume } = useComposerBridge.getState();
    // Same source of truth as `handleEmojiPick`: the DOM value IS the
    // committed draft. The insertion point is the selection END — a range
    // selected before the click (invisible once the textarea blurred) is
    // kept, never replaced.
    let draft = el.value;
    let caret = el.selectionEnd ?? draft.length;
    let inserted = false;
    for (const request of requests) {
      consume(request.id);
      if (request.conversationId !== conversationId) {
        toast('输入框已切换到其他会话，未插入');
        continue;
      }
      const next = insertAtCaret(draft, caret, request.text);
      draft = next.value;
      caret = next.caret;
      inserted = true;
    }
    if (!inserted) return;
    pendingCaretRef.current = caret;
    setText(draft);
  }, [insertRequests, conversationId]);

  // Close the emoji panel on any mousedown outside it. Registered only
  // while open. The toggle button is excluded so its own click still sees
  // `emojiOpen === true` and toggles CLOSED (otherwise this listener would
  // close on mousedown and the click would immediately re-open).
  useEffect(() => {
    if (!emojiOpen) return;
    function onDocMouseDown(e: MouseEvent): void {
      const target = e.target as Node;
      if (emojiPanelRef.current?.contains(target)) return;
      if (emojiButtonRef.current?.contains(target)) return;
      setEmojiOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [emojiOpen]);

  /** Insert the picked emoji at the textarea caret (falling back to the end
   *  when there is no selection to read), then queue the caret restore.
   *  Reads `el.value` rather than closing over `text` so the callback is
   *  referentially stable — the memoized picker (its ~760 buttons) then
   *  skips re-rendering on every composer keystroke. For a controlled
   *  textarea `el.value` is identical to `text` at event time. The caret
   *  math uses `emoji.length` (UTF-16 units), not 1 — most emoji are
   *  surrogate pairs and some carry a variation selector. */
  const handleEmojiPick = useCallback((emoji: string): void => {
    const el = textareaRef.current;
    if (!el) {
      setText((prev) => prev + emoji);
      return;
    }
    const value = el.value;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    pendingCaretRef.current = start + emoji.length;
    setText(value.slice(0, start) + emoji + value.slice(end));
  }, []);

  /** Stable close callback for the panel's own Escape handling. */
  const closeEmojiPanel = useCallback((): void => {
    setEmojiOpen(false);
  }, []);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] ?? null;
    // Always reset the input value so picking the same file twice still
    // fires `change` the second time.
    e.target.value = '';
    if (!picked) return;
    if (picked.size > MAX_UPLOAD_BYTES) {
      toast(`File is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB)`);
      return;
    }
    setFile(picked);
  }

  function clearAttachment(): void {
    setFile(null);
  }

  /**
   * Clipboard paste handler for the textarea.
   *
   * - Passes text paste through by NOT calling `preventDefault()`.
   * - Intercepts image items (`kind === 'file'`, `type.startsWith('image/')`).
   * - If no file is attached yet, adopts the first pasted image, giving it a
   *   sane name if the browser omits one.
   * - If a file is already attached, ignores the paste and shows a toast.
   * - The same 25 MB size cap applied by the attachment button is reused.
   */
  function handlePaste(e: ClipboardEvent<HTMLTextAreaElement>): void {
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(
      (item) => item.kind === 'file' && item.type.startsWith('image/'),
    );

    if (!imageItem) {
      // No image in the clipboard — let the browser handle text paste normally.
      return;
    }

    const raw = imageItem.getAsFile();
    if (!raw) return;

    // Prevent the paste from inserting garbled binary chars into the textarea.
    e.preventDefault();

    if (file !== null) {
      toast('Replace the current attachment before pasting another image');
      return;
    }

    if (raw.size > MAX_UPLOAD_BYTES) {
      toast(`Pasted image is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB)`);
      return;
    }

    // Browsers frequently name clipboard images "image.png" or leave the name
    // blank — normalise to a timestamped fallback when needed.
    let name = raw.name && raw.name !== '' ? raw.name : '';
    if (!name || name === 'image.png') {
      const ext = raw.type === 'image/png' ? 'png'
        : raw.type === 'image/jpeg' ? 'jpg'
        : raw.type === 'image/gif' ? 'gif'
        : raw.type === 'image/webp' ? 'webp'
        : raw.type.split('/')[1] ?? 'png';
      name = `clipboard-${Date.now()}.${ext}`;
    }

    const namedFile = name !== raw.name
      ? new File([raw], name, { type: raw.type, lastModified: raw.lastModified })
      : raw;

    setFile(namedFile);
  }

  async function handleSend(): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed && !file) return;
    if (send.isPending) return; // belt-and-braces; the button is also disabled

    const clientRef = crypto.randomUUID();
    // Snapshot inputs before clearing — `mutate` reads them off the
    // closure. Optimistic write happens synchronously in `onMutate`, so
    // the bubble appears in the same render the inputs clear.
    const textToSend = trimmed || undefined;
    const fileToSend = file ?? undefined;
    const replyTo = replyingTo?.platform_message_id ?? null;

    setText('');
    setFile(null);
    onCancelReply?.();

    send.mutate({
      conversationId,
      text: textToSend,
      file: fileToSend,
      clientRef,
      replyTo,
      threadKey,
      accountId,
    });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    // Enter sends only when the `enterSend` pref is on. When it's off, a bare
    // Enter falls through to the textarea's default (inserts a newline) and the
    // user sends via the button. Shift+Enter always inserts a newline (it never
    // matches this branch), so it is unchanged in both modes.
    if (enterSend && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
      return;
    }
    if (e.key === 'Escape') {
      // Handled locally — stop the event from reaching ChatPanelInner's
      // document-level Escape listener (which would otherwise close the
      // conversation on the same keystroke). See the component doc comment.
      e.stopPropagation();
      // Highest priority: an open emoji panel absorbs the Escape outright —
      // the reply banner / attachment stay untouched (next press handles
      // them).
      if (emojiOpen) {
        setEmojiOpen(false);
        return;
      }
      if (replyingTo) {
        onCancelReply?.();
        return;
      }
      if (file) {
        clearAttachment();
        return;
      }
      textareaRef.current?.blur();
    }
  }

  /**
   * Escape backstop on the composer root for keystrokes that do NOT come
   * from the textarea — the panel subtree (search input, tabs, emoji
   * buttons) and the toggle button itself. The panel's own handler already
   * stops propagation before this runs for panel-internal Escapes, so in
   * practice this catches the toggle-button-focused case; either way an
   * Escape while the panel is open must never bubble to ChatPanelInner's
   * close-conversation listener. (Textarea Escapes never reach here —
   * `handleKeyDown` always stops their propagation.)
   */
  function handleRootKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape' && emojiOpen) {
      e.stopPropagation();
      setEmojiOpen(false);
    }
  }

  const canSend = (text.trim().length > 0 || file !== null) && !send.isPending;

  return (
    // Neumorphic: borderless composer strip; controls carry their own
    // raised / inset chrome (design §右栏 输入区). `relative` anchors the
    // emoji panel, which floats above the strip (and over the message list).
    <div className="relative shrink-0 bg-transparent px-5 pb-4 pt-2" onKeyDown={handleRootKeyDown}>
      {/* Emoji panel — absolutely anchored above the strip, left-aligned
          with the smiley button, floating over the message list. The
          wrapper div mounts synchronously (Suspense only wraps the lazy
          panel) so the outside-click ref is live even while the chunk is
          still loading. */}
      {emojiOpen && (
        <div ref={emojiPanelRef} className="absolute bottom-full left-5 z-30 mb-2">
          <Suspense fallback={null}>
            <EmojiPicker onPick={handleEmojiPick} onClose={closeEmojiPanel} />
          </Suspense>
        </div>
      )}

      {/* Reply banner — above the attachment chip / textarea row. */}
      {replyingTo && (
        <div className="mb-2 flex items-center gap-2 rounded-xl px-3 py-2 text-xs shadow-neu-inset-sm">
          <Reply className="size-4 shrink-0 text-muted-foreground" />
          <div className="flex min-w-0 flex-col">
            <span className="font-medium">回复 {quotedSenderLabel(replyingTo)}</span>
            <span className="truncate text-muted-foreground">
              {quotedPreviewText(replyingTo)}
            </span>
            {replyHint && (
              <span
                data-testid="thread-picker-locked-hint"
                className="mt-0.5 text-[10px] font-semibold leading-none text-muted-foreground"
              >
                {replyHint}
              </span>
            )}
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => onCancelReply?.()}
            aria-label="取消回复"
            className="ml-auto size-5 shrink-0"
          >
            <X className="size-3" />
          </Button>
        </div>
      )}

      {/* Attachment chip — above the textarea row. */}
      {file && (
        <div className="mb-2 flex items-center gap-2 rounded-xl px-3 py-2 text-xs shadow-neu-inset-sm">
          {/* Thumbnail for images; generic icon for other file types. */}
          {blobUrl ? (
            <img
              src={blobUrl}
              alt={file.name}
              className="h-16 w-auto shrink-0 rounded object-cover"
            />
          ) : (
            <FileText className="size-8 shrink-0 text-muted-foreground" />
          )}
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium" title={file.name}>
              {file.name}
            </span>
            <span className="text-muted-foreground">
              {(file.size / 1024).toFixed(0)} KB
            </span>
          </div>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={clearAttachment}
            aria-label="Remove attachment"
            className="ml-auto size-5 shrink-0"
          >
            <X className="size-3" />
          </Button>
        </div>
      )}

      <div className="flex items-end gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,application/*"
          onChange={handleFileChange}
          className="hidden"
          aria-hidden="true"
        />
        {/* Emoji toggle — WhatsApp order: smiley first, then attach. Reads
            inset/active (aria-pressed) while the panel is open, mirroring
            the 客户资料 toggle in the chat header. */}
        <Button
          ref={emojiButtonRef}
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => setEmojiOpen((open) => !open)}
          aria-label="表情"
          aria-pressed={emojiOpen}
          className={cn(
            'size-11 shrink-0 rounded-[14px]',
            emojiOpen
              ? 'text-primary shadow-neu-inset-press'
              : 'shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press',
          )}
        >
          <Smile className="size-4" />
        </Button>

        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach file"
          className="size-11 shrink-0 rounded-[14px] shadow-neu-raised hover:shadow-neu-raised-hover active:shadow-neu-inset-press"
        >
          <Paperclip className="size-4" />
        </Button>

        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={
            enterSend
              ? 'Type a message — Enter to send, Shift+Enter for newline'
              : 'Type a message — Enter for newline; use the send button'
          }
          rows={1}
          className="min-h-[44px] resize-none rounded-2xl px-5 py-3"
          aria-label="Message composer"
        />

        {/* Thread-mode account picker slot (or any future accessory) — sits
            immediately left of the send button. */}

        <Button
          type="button"
          size="icon"
          onClick={() => void handleSend()}
          disabled={!canSend}
          aria-label="Send message"
          className="size-12 shrink-0 rounded-2xl"
        >
          {send.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <SendHorizontal className="size-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
